/* global BigInt */
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import PackOddsModal from '../components/PackOddsModal';
import {
    ipfsGateway, resolveIpfsUrl,
    tokenContracts,
    tokenDecimals,
    STORE_CONTRACT
} from '../constants/gameConfig';

const WAX_RPC_ENDPOINTS = [
    "https://wax.greymass.com",
    "https://api.wax.alohaeos.com",
    "https://wax.eosphere.io",
    "https://wax.eosdac.io",
    "https://wax.eu.eosamsterdam.net"
];

// Reads a whole table from the store contract, trying each node in turn.
// Returns null if no node answered (or the table doesn't exist yet).
async function fetchStoreTable(table) {
    for (const endpoint of WAX_RPC_ENDPOINTS) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 4000);

            // 🛡️ FIX: No explicit headers bypasses the CORS preflight check completely
            const response = await fetch(`${endpoint}/v1/chain/get_table_rows`, {
                method: "POST",
                body: JSON.stringify({
                    json: true,
                    code: STORE_CONTRACT,
                    scope: STORE_CONTRACT,
                    table,
                    limit: 500
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                return data.rows;
            }
        } catch (err) {
            console.warn(`Node ${endpoint} failed, trying next...`);
        }
    }
    return null;
}

// Mirrors whatifstores::pegged_amount: the listing's WAX price converted at
// the oracle rate, less the token's discount, rounded up.
function pegPriceOption(refWax, oracle) {
    const [precisionStr, symbol] = oracle.token.sym.split(',');
    const precision = Number(precisionStr);
    const refRaw = BigInt(refWax.split(' ')[0].replace('.', ''));
    const num = refRaw * BigInt(10000 - oracle.discount_bps) * BigInt(10) ** BigInt(precision);
    const rate = BigInt(String(oracle.rate_e12));
    const raw = ((num + rate - BigInt(1)) / rate).toString().padStart(precision + 1, '0');
    const amount = precision > 0 ? `${raw.slice(0, -precision)}.${raw.slice(-precision)}` : raw;
    return {
        price: parseFloat(amount),
        tokenSymbol: symbol,
        contract: oracle.token.contract,
        quantity: `${amount} ${symbol}`,
        discountPct: oracle.discount_bps / 100
    };
}

// Payment options for one listing. Its WAX price is the reference: every
// token with an enabled oracle is charged that price converted and
// discounted; WAX and any other token keep their static prices, as in the
// contract.
function buildPriceOptions(row, oracles) {
    const staticPrices = row.prices.map(p => {
        const parts = p.quantity.split(" ");
        return {
            price: parseFloat(parts[0]),
            tokenSymbol: parts[1],
            contract: p.contract,
            quantity: p.quantity
        };
    });
    const wax = staticPrices.find(p => p.tokenSymbol === 'WAX' && p.contract === 'eosio.token');
    if (!wax || oracles.length === 0) return staticPrices;

    const pegged = oracles
        .map(o => pegPriceOption(wax.quantity, o))
        .sort((a, b) => a.tokenSymbol.localeCompare(b.tokenSymbol));
    const covered = new Set(pegged.map(p => p.tokenSymbol));
    const others = staticPrices.filter(p => p.tokenSymbol !== 'WAX' && !covered.has(p.tokenSymbol));
    return [...pegged, ...others, wax];
}

// Enabled price feeds. Empty before the store contract has them, which
// leaves every listing on its static prices.
async function fetchOracles() {
    const rows = await fetchStoreTable("oracles");
    return (rows || []).filter(o => o.enabled && String(o.rate_e12) !== "0");
}

function StorePage({ session, refreshNfts }) {
    const [storeItems, setStoreItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [feedback, setFeedback] = useState('');
    const [isPurchasing, setIsPurchasing] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState({});
    const [purchaseLogs, setPurchaseLogs] = useState({});
    const [unboxConfigs, setUnboxConfigs] = useState([]);
    const [oddsModalData, setOddsModalData] = useState({
        isOpen: false,
        unboxConfig: null,
        itemName: '',
        itemImg: ''
    });

    useEffect(() => {
        // Fetch active unbox configs to display drop odds for packs
        const q = query(collection(db, 'unbox_configs'), where('isActive', '==', true));
        const unsubscribeUnbox = onSnapshot(q, (snapshot) => {
            setUnboxConfigs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            console.warn("Could not load unbox configs:", err);
        });
        return () => unsubscribeUnbox();
    }, []);

    useEffect(() => {
        const fetchStoreItems = async () => {
            setIsLoading(true);

            const [listingRows, oracles] = await Promise.all([fetchStoreTable("listings"), fetchOracles()]);

            if (!listingRows) {
                setFeedback("Failed to fetch store listings. Nodes may be overloaded, or contract ABI is uninitialized.");
                setIsLoading(false);
                return;
            }

            const itemsFromDb = listingRows.map(row => ({
                listingId: row.listing_id,
                templateId: row.template_id,
                collectionName: row.collection_name,
                purchaseLimit: row.max_supply,
                currentSupply: row.current_supply,
                rawRow: row,
                prices: buildPriceOptions(row, oracles)
            }));

            const atomicApiEndpoints = [
                "https://wax-atomic-api.eosphere.io",
                "https://wax.api.atomicassets.io",
                "https://atomic.wax.eosrio.io",
                "https://aa.neftyblocks.com",
                "https://aa.dapplica.io",
                "https://wax-aa.eosamsterdam.net"
            ];

            let enrichedItems = [];
            for (const endpoint of atomicApiEndpoints) {
                try {
                    const promises = itemsFromDb.map(async item => {
                        const controller = new AbortController();
                        const timeoutId = setTimeout(() => controller.abort(), 4000);

                        try {
                            const res = await fetch(`${endpoint}/atomicassets/v1/templates/${item.collectionName}/${item.templateId}`, { signal: controller.signal });
                            clearTimeout(timeoutId);
                            const data = await res.json();
                            if (data.success) {
                                return { ...item, templateData: data.data };
                            }
                        } catch (e) {
                            clearTimeout(timeoutId);
                        }
                        return null;
                    });

                    const results = await Promise.all(promises);
                    enrichedItems = results.filter(item => item !== null);
                    if (enrichedItems.length === itemsFromDb.length) break;
                } catch (e) {
                    console.warn(`Failed to fetch template data from ${endpoint}`, e);
                }
            }

            setStoreItems(enrichedItems);
            setIsLoading(false);
        };

        fetchStoreItems();
    }, []);

    useEffect(() => {
        if (!session?.actor) {
            setPurchaseLogs({});
            return;
        }
        const logsRef = collection(db, `players/${session.actor.toString()}/purchase_logs`);
        const unsubscribeLogs = onSnapshot(logsRef, (snapshot) => {
            const logs = {};
            snapshot.forEach(doc => {
                logs[doc.id] = doc.data().count;
            });
            setPurchaseLogs(logs);
        });
        return () => unsubscribeLogs();
    }, [session]);

    const getMatchingUnboxConfig = (item) => {
        return unboxConfigs.find(c => String(c.packTemplateId).trim() === String(item.templateId).trim());
    };

    const handlePurchase = async (item) => {
        if (!session || !item.templateData) {
            setFeedback("Error: Cannot complete purchase. Please log in.");
            return;
        }

        const paymentSelection = selectedPayment[item.templateId] || item.prices[0].tokenSymbol;
        let priceOption = item.prices.find(p => p.tokenSymbol === paymentSelection);

        if (priceOption && priceOption.discountPct) {
            priceOption = buildPriceOptions(item.rawRow, await fetchOracles())
                .find(p => p.tokenSymbol === paymentSelection) || priceOption;
        }

        if (!priceOption) {
            setFeedback("Error: Please select a valid payment option.");
            return;
        }

        if (!window.confirm(`Are you sure you want to buy "${item.templateData.immutable_data.name}" for ${priceOption.price.toLocaleString()} ${priceOption.tokenSymbol}?`)) {
            return;
        }

        if (item.purchaseLimit > 0) {
            const currentCount = purchaseLogs[item.templateId] || 0;
            if (currentCount >= item.purchaseLimit) {
                setFeedback(`Error: You have already reached the purchase limit of ${item.purchaseLimit} for this item.`);
                return;
            }
        }

        setIsPurchasing(item.templateId);
        setFeedback(`Step 1 of 3: Please approve the transfer of ${priceOption.price.toLocaleString()} ${priceOption.tokenSymbol} in your wallet.`);

        try {
            const memo = `BUY:${item.listingId}`;
            const quantity = priceOption.quantity || `${priceOption.price.toFixed(tokenDecimals[priceOption.tokenSymbol])} ${priceOption.tokenSymbol}`;
            const contract = priceOption.contract || tokenContracts[priceOption.tokenSymbol];

            const actions = [{
                account: contract,
                name: 'transfer',
                authorization: [session.permissionLevel],
                data: {
                    from: session.actor.toString(),
                    to: STORE_CONTRACT,
                    quantity,
                    memo
                },
            }];

            const txResult = await session.transact({ actions });
            setFeedback("Step 2 of 3: Transaction sent. Waiting for network confirmation...");

            await new Promise(resolve => setTimeout(resolve, 3000));

            setFeedback("Step 3 of 3: Verifying purchase... Refreshing your inventory, please wait...");
            if (refreshNfts) await refreshNfts();

            setFeedback("Purchase successful and inventory refreshed!");
            setTimeout(() => setFeedback(''), 5000);
        } catch (error) {
            console.error("Purchase failed:", error);
            setFeedback(`Error: ${error.message}`);
        } finally {
            setIsPurchasing(null);
        }
    };

    return (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px', maxWidth: '1000px', margin: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', borderBottom: '1px solid #333', paddingBottom: '16px' }}>
                <div>
                    <h2 style={{ margin: 0 }}>Item Store</h2>
                    <p style={{ margin: '4px 0 0 0', color: '#ccc', fontSize: '0.9rem' }}>
                        Purchase Game Packs and Gear directly with Game Tokens.
                    </p>
                </div>
            </div>

            {/* WHATIFX Aggregator Embedded Widget (Web-only: hidden on native Android to comply with Google Play personal account policies) */}
            {!Capacitor.isNativePlatform() && (
                <div className="w-full flex justify-center my-8" style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                    <iframe
                        src="https://whatifxchange.rewardsinwhatif.app/?in=WAX_eosio.token&out=FIREMAN_yoyinsoly214&partner=whatifreward&widget=true"
                        width="100%"
                        height="700px"
                        frameBorder="0"
                        allow="camera; clipboard-read; clipboard-write"
                        title="WHATIFX Swap Widget"
                        style={{ borderRadius: '24px', border: '1px solid #1f2937', background: 'transparent', maxWidth: '600px', width: '100%', overflow: 'hidden' }}
                    ></iframe>
                </div>
            )}

            {feedback && (
                <p style={{ color: feedback.startsWith('Error') || feedback.startsWith('Retry Error') || feedback.startsWith('Failed') ? '#ff4b2b' : 'lightgreen', backgroundColor: '#222', border: '1px solid #444', padding: '12px', borderRadius: '6px', fontWeight: 'bold' }}>
                    {feedback}
                </p>
            )}

            {Capacitor.isNativePlatform() ? (
                <div style={{
                    textAlign: 'center',
                    padding: '30px 20px',
                    backgroundColor: '#111',
                    border: '1px solid #ff9800',
                    borderRadius: '8px',
                    maxWidth: '640px',
                    margin: '20px auto 40px auto',
                    color: '#eee',
                    boxShadow: '0 0 20px rgba(255, 152, 0, 0.2)'
                }}>
                    <div style={{ fontSize: '42px', marginBottom: '12px' }}>🎮</div>
                    <p style={{ color: '#aaa', fontSize: '0.9rem', lineHeight: '1.6', margin: '0 auto 16px', maxWidth: '520px' }}>
                        NFT and token purchases aren't available in the Android app.
                    </p>
                    <p style={{ color: '#888', fontSize: '0.8rem', margin: 0 }}>
                        Stamina, character equipment, dungeon expeditions, and leaderboards continue here.
                    </p>
                </div>
            ) : isLoading ? (
                <p style={{ textAlign: 'center', padding: '40px', color: '#00e5ff' }}>Loading store items from blockchain...</p>
            ) : storeItems.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '20px', marginTop: '20px' }}>
                    {storeItems.map(item => {
                        const currentPurchaseCount = purchaseLogs[item.templateId] || 0;
                        const hasReachedLimit = item.purchaseLimit > 0 && currentPurchaseCount >= item.purchaseLimit;
                        const matchingConfig = getMatchingUnboxConfig(item);

                        return (
                            <div key={item.templateId} style={{ border: '1px solid #444', borderRadius: '8px', padding: '14px', textAlign: 'center', background: 'rgba(255,255,255,0.04)', display: 'flex', flexDirection: 'column' }}>
                                <img
                                    src={resolveIpfsUrl(item.templateData.immutable_data.img)}
                                    alt={item.templateData.immutable_data.name}
                                    style={{ width: '100%', height: '180px', objectFit: 'contain', borderRadius: '4px', marginBottom: '8px' }}
                                />
                                <h4 style={{ margin: '6px 0', fontSize: '1rem', color: '#fff' }}>{item.templateData.immutable_data.name}</h4>
                                <p style={{ fontSize: '12px', color: '#aaa', margin: '0 0 4px 0' }}>
                                    {item.purchaseLimit > 0 ? `Limit: ${item.purchaseLimit} per account` : 'Unlimited Purchases'}
                                </p>

                                {item.purchaseLimit > 0 && (
                                    <p style={{ fontSize: '12px', color: hasReachedLimit ? 'orange' : '#aaa', margin: '0 0 10px 0' }}>
                                        Your Count: {currentPurchaseCount} / {item.purchaseLimit}
                                    </p>
                                )}

                                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {/* 📊 VIEW DROP ODDS BUTTON (Google Play Randomized Item Policy Compliance) */}
                                    {matchingConfig && (
                                        <button
                                            type="button"
                                            onClick={() => setOddsModalData({
                                                isOpen: true,
                                                unboxConfig: matchingConfig,
                                                itemName: item.templateData.immutable_data.name,
                                                itemImg: resolveIpfsUrl(item.templateData.immutable_data.img)
                                            })}
                                            style={{
                                                width: '100%',
                                                padding: '6px 10px',
                                                backgroundColor: 'rgba(255, 152, 0, 0.12)',
                                                border: '1px solid #ff9800',
                                                color: '#ff9800',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            📊 VIEW DROP ODDS
                                        </button>
                                    )}

                                    {/* Token / Crypto Purchase (Available on Web and Connected Wallets) */}
                                    <select
                                        value={selectedPayment[item.templateId] || item.prices[0].tokenSymbol}
                                        onChange={(e) => setSelectedPayment(prev => ({ ...prev, [item.templateId]: e.target.value }))}
                                        className="modern-input"
                                        style={{ width: '100%', fontSize: '0.78rem', padding: '4px' }}
                                        disabled={isPurchasing !== null || hasReachedLimit}
                                    >
                                        {item.prices.map(p => (
                                            <option key={p.tokenSymbol} value={p.tokenSymbol}>
                                                {p.price.toLocaleString()} {p.tokenSymbol}{p.discountPct ? ` (${p.discountPct}% off)` : ''}
                                            </option>
                                        ))}
                                    </select>

                                    <button
                                        onClick={() => handlePurchase(item)}
                                        disabled={isPurchasing !== null || hasReachedLimit}
                                        className="modern-button"
                                        style={{ width: '100%', fontSize: '0.8rem', padding: '7px', background: hasReachedLimit ? '#555' : 'linear-gradient(145deg, #28a745, #218838)', cursor: hasReachedLimit ? 'not-allowed' : 'pointer' }}
                                    >
                                        {isPurchasing === item.templateId ? 'Processing...' : hasReachedLimit ? 'Limit Reached' : 'Buy with Tokens'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p style={{ textAlign: 'center', color: '#888' }}>There are currently no items for sale. Please check back later!</p>
            )}

            {/* Pack Odds Transparency Modal (Google Play Store Loot Box Policy Compliance) */}
            <PackOddsModal
                isOpen={oddsModalData.isOpen}
                onClose={() => setOddsModalData({ isOpen: false, unboxConfig: null, itemName: '', itemImg: '' })}
                unboxConfig={oddsModalData.unboxConfig}
                itemName={oddsModalData.itemName}
                itemImg={oddsModalData.itemImg}
            />
        </div>
    );
}

export default StorePage;